<style lang="postcss">
	.credit-card-cover-amounts {
		display: flex;
		flex-direction: column;
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
	}
	.card-cover-inner {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}
	.grey-text {
		opacity: 0.5;
	}
	.some-text {
		margin: 0.25rem 0;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		gap: 0.5rem;
	}
	.proportional-nums {
		font-variant-numeric: proportional-nums;
	}
	.credit-card-cover-field {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: flex-start;
		flex: 1 1 0%;
		min-width: 0px;
	}
	.coupon-label {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 0.25rem;
	}
	.coupon-input {
		display: flex;
		flex: 1 1 0%;
		min-width: 0px;
		outline: none;
		background-color: transparent;
	}
	.coupon-input:focus {
		outline: none;
	}
	.coupon-right {
		display: flex;
		flex-direction: row;
		justify-content: center;
		width: 1.5rem;
	}
	.coupon-button {
		padding: 0.25rem 0.5rem;
		font-size: 0.75rem;
		border-radius: 0.25rem;
	}
</style>

<script lang="ts">
	import { BROWSER } from "esm-env";
	import type { Writable } from "svelte/store";
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	import { createEventDispatcher, getContext, onDestroy } from "svelte";
	import { slide } from "$lib/transitions/slide";
	import { listToArray } from "$lib/helpers/math";
	import { debounce } from "$lib/helpers/functions";

	export let field: FieldDisplay & { type: "CreditCard" };

	import type { t as tBase } from "$lib/helpers/translate";
	const t = getContext<typeof tBase>("t");

	const dispatch = createEventDispatcher();
	let nf = new Intl.NumberFormat(undefined, {
		currencyDisplay: "symbol",
		style: "currency",
		currency: field.settings.currency ?? "USD",
		trailingZeroDisplay: "stripIfInteger"
	} as Intl.NumberFormatOptions);
	let stripe = getContext<Writable<StripeInstance>>("stripe");
	let formStripe = getContext<Writable<FormStripe>>("formStripe");
	let visit = getContext<Writable<Visit>>("visit");
	const formLanguage = getContext<Language>("formLanguage");

	let computedValue: string;

	let paymentElement: any;
	let paymentElementDiv: HTMLDivElement;
	let focus: boolean = false;
	let style: any;
	let couponCode: string = "";
	let couponError: string | null = null;
	let couponLoading: boolean = false;
	let plansLoading: boolean = false;
	let plansComputed: any = [];
	let ratesComputed: any = [];

	$: if ($stripe && paymentElementDiv) loadStripeElements();
	$: computedValue = field.valueComputed;
	$: computedRates = field.taxRatesComputed;
	$: debouncedFetchPlanNames(computedValue);
	$: if (computedRates) debouncedFetchTaxRates(computedRates);

	const fetchPlanNames = async (computedValue: string) => {
		const plans = listToArray(computedValue, formLanguage.locale);
		if (field.settings.type !== "subscription" || !BROWSER) return;
		if (plans.length === 0) {
			plansComputed = [];
			return;
		}
		plansLoading = true;
		const fetchObj = await fetch(
			`/public/form/integrations/stripe/get-plan-names?plans=${JSON.stringify(
				plans
			)}&form_id=${$visit.form_id}&test=${$visit.test}&stripeTest=${
				field.settings.test
			}`
		);
		const response = await fetchObj.json();
		plansLoading = false;
		if (response.data) {
			plansComputed = response.data;
			return;
		}
	};

	const fetchTaxRates = async (computedValue: string) => {
		const rates = listToArray(computedValue, formLanguage.locale);
		if (field.settings.type !== "subscription" || !BROWSER) return;
		if (rates.length === 0) {
			ratesComputed = [];
			return;
		}
		plansLoading = true;
		const fetchObj = await fetch(
			`/public/form/integrations/stripe/get-tax-rates?rates=${JSON.stringify(
				rates
			)}&form_id=${$visit.form_id}&test=${$visit.test}&stripeTest=${
				field.settings.test
			}`
		);
		const response = await fetchObj.json();
		plansLoading = false;
		if (response.data) {
			ratesComputed = response.data;
			return;
		}
	};

	const debouncedFetchPlanNames = debounce(fetchPlanNames, 500);
	const debouncedFetchTaxRates = debounce(fetchTaxRates, 500);

	const loadStripeElements = async () => {
		if ($stripe === null) {
			console.warn("Stripe keys not found");
			return;
		}
		const parentStyles = getComputedStyle(paymentElementDiv);
		paymentElementDiv.style.height = parentStyles.height;
		paymentElementDiv.innerHTML = "";
		style = {
			base: {
				color: parentStyles.color,
				fontFamily: parentStyles.fontFamily,
				fontSize: parentStyles.fontSize,
				"::placeholder": {
					color: getComputedStyle(paymentElementDiv, "::placeholder").color
				}
			}
		};
		$stripe.elements = $stripe.instance.elements();
		paymentElement = $stripe.elements.create("card", {
			hideIcon: false,
			style
		});
		paymentElement.mount(paymentElementDiv);
		paymentElement.addEventListener("focus", () => {
			focus = true;
			dispatch("updateOther", {
				active: true
			});
		});
		paymentElement.addEventListener("blur", () => {
			focus = false;
			dispatch("updateOther", {
				tainted: true
			});
			dispatch("updateOther", {
				active: false
			});
		});
	};

	const applyCoupon = async () => {
		couponError = null;
		if (couponCode.trim() === "") {
			couponError = t.get("invalid");
			return;
		}
		couponLoading = true;
		const fetchObj = await fetch(
			`/public/form/integrations/stripe/get-coupon`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					coupon: couponCode,
					form_id: $visit.form_id,
					currency: field.settings.currency,
					test: $visit.test,
					stripeTest: field.settings.test
				})
			}
		);
		const response = await fetchObj.json();
		couponLoading = false;
		if (response.error) {
			couponError = response.error;
			return;
		}
		$formStripe.coupon = response.data;
	};

	const formatNumber = (amount: string, currency: string) => {
		if (isNaN(parseFloat(amount))) return "Unknown price";
		const nf = new Intl.NumberFormat(undefined, {
			currencyDisplay: "symbol",
			style: "currency",
			currency: currency,
			trailingZeroDisplay: "stripIfInteger"
		} as Intl.NumberFormatOptions);
		return nf.format(parseFloat(amount));
	};

	const removeCoupon = async () => {
		couponError = null;
		couponCode = "";
		$formStripe.coupon = undefined;
	};

	onDestroy(() => {
		if (paymentElement) {
			paymentElement.unmount();
		}
	});
</script>

<div class="field-label-cover credit-card-cover-amounts">
	{#if field.settings.type === "charge"}
		{#if field.discountComputed && field.discountComputed !== "0"}
			<div transition:slide class="some-text">
				<div class="grey-text">{t.get("sub_total")}</div>
				<div class="proportional-nums">
					{nf.format(
						parseFloat(
							field.subTotalComputed.trim() !== ""
								? field.subTotalComputed
								: "0"
						)
					)}
				</div>
			</div>
			<div transition:slide class="some-text">
				<div class="grey-text">Discount</div>
				<div class="proportional-nums">
					-{nf.format(
						parseFloat(
							field.discountComputed.trim() !== ""
								? field.discountComputed
								: "0"
						)
					)}
				</div>
			</div>
		{/if}
		<div transition:slide class="some-text">
			<div class="grey-text">{t.get("total")}</div>
			<div class="proportional-nums">
				{nf.format(
					parseFloat(
						field.valueComputed.trim() !== "" ? field.valueComputed : "0"
					)
				)}
			</div>
		</div>
	{:else}
		{#each plansComputed as plan}
			<div transition:slide class="some-text">
				<div class="grey-text">{plan.name}</div>
				<div class="proportional-nums">
					{formatNumber(plan.amount, plan.currency)}
				</div>
			</div>
		{/each}
		{#each ratesComputed as rate}
			<div transition:slide class="some-text">
				<div class="grey-text">Tax</div>
				<div>{rate.name}</div>
			</div>
		{/each}
		{#if $formStripe.coupon}
			<div transition:slide class="some-text">
				<div class="grey-text">Coupon</div>
				<div>{$formStripe.coupon.name}</div>
			</div>
		{/if}
	{/if}
</div>

<div class="credit-card-cover-field field-input-cover">
	<div class="card-cover-inner">
		<input
			name="{field.id}"
			type="hidden"
			value="{field.valueComputed || ''}" />
		<div
			class:focus="{focus}"
			style="align-items: center; display: grid"
			class="field-input"
			bind:this="{paymentElementDiv}">
			<span class="placeholder">...</span>
		</div>
		{#if field.settings.enableCoupons}
			<form on:submit|preventDefault="{applyCoupon}">
				<label class="coupon-label field-input">
					<input
						spellcheck="false"
						autocomplete="new-password"
						class="coupon-input"
						placeholder="{t.get('coupon_code')}"
						type="text"
						bind:value="{couponCode}" />
					<div class="coupon-right">
						{#if couponLoading}
							<div class="loader"></div>
						{:else if couponError}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								width="1.25rem"
								height="1.25rem"
								style="color: rgb(220 38 38 / 1)"
								viewBox="0 0 24 24"
								stroke-width="1.5"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								></path>
							</svg>
						{:else if $formStripe.coupon}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1.25rem"
								height="1.25rem"
								style="color: rgb(21 128 61 / 1)"
								viewBox="0 0 256 256"
								><rect width="256" height="256" fill="none"></rect><polyline
									points="88 136 112 160 168 104"
									fill="none"
									stroke="currentColor"
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="{16}"></polyline
								><circle
									cx="128"
									cy="128"
									r="96"
									fill="none"
									stroke="currentColor"
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="{16}"></circle
								></svg>
						{/if}
					</div>
					{#if $formStripe.coupon}
						<button
							on:click="{removeCoupon}"
							disabled="{couponLoading}"
							style="opacity: {couponLoading ? '0.6' : '1'}"
							class="coupon-button button-no-pad"
							type="button">Remove</button>
					{:else}
						<button
							disabled="{couponLoading}"
							style="opacity: {couponLoading ? '0.6' : '1'}"
							class="coupon-button button-no-pad"
							type="submit">{t.get("apply")}</button>
					{/if}
				</label>
			</form>
		{/if}
	</div>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
